'use client';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import type { LiveStreamCtaSource } from '@fanduel/casino-streaming-um-web';
import { useAccountUm } from '@/hooks/use-account-um';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { ddRumError } from '@/utils/datadog';
import { DD_USE_CASES } from '@/utils/datadog/types';
import {
    getZoneCtaClickedProps,
    LIVESTREAM_ENTRY_POINT_LINK_TEXT,
    LIVESTREAM_ENTRY_POINT_MODULE
} from '@/tracking/amplitude/events/general/livestream-entry-point';
import { LIVESTREAM_PAGE_ENTRY_POINT_DEFAULTS } from './constants';
import type { LivestreamPageEntryPointProps } from './types';
import './styles/@theme';

const LiveStreamEntryPoint = dynamic(
    () => import('@fanduel/casino-streaming-um-web').then((module) => module.LiveStreamEntryPoint),
    { ssr: false }
);

type LivestreamPageLobbyEntryPointProps = LivestreamPageEntryPointProps & {
    backgroundImageUrl?: string;
    promoImageUrl?: string;
};

export const LivestreamPageLobbyEntryPoint = ({
    backgroundImageUrl,
    promoImageUrl,
    categoryPath,
    label,
    previewImageUrl,
    avatarUrl,
    previewTitle = 'Casino Livestream'
}: LivestreamPageLobbyEntryPointProps) => {
    const router = useRouter();
    const { isLoggedIn } = useGlobalContext();
    const { triggerLogin } = useAccountUm();
    const { sendEvent } = useStore((state) => state.tracking);

    if (!backgroundImageUrl || !promoImageUrl || !previewImageUrl || !categoryPath) {
        // Missing required data indicates a CMS configuration problem, so it's worth reporting to Datadog RUM
        // from the client to help diagnose broken livestream entry points reported from the field.
        ddRumError({
            name: 'LIVESTREAM_PAGE_ENTRY_POINT_MISSING_CMS_DATA',
            description: 'Livestream page entry point is missing required CMS data',
            use_case: DD_USE_CASES.STREAMING,
            context: {
                hasBackgroundImageUrl: Boolean(backgroundImageUrl),
                hasPromoImageUrl: Boolean(promoImageUrl),
                hasPreviewImageUrl: Boolean(previewImageUrl),
                hasCategoryPath: Boolean(categoryPath)
            }
        });
        return null;
    }

    const handleCtaClick = (source?: LiveStreamCtaSource) => {
        const eventProps = getZoneCtaClickedProps({
            module: LIVESTREAM_ENTRY_POINT_MODULE.LOBBY,
            linkText:
                source === 'thumbnail'
                    ? LIVESTREAM_ENTRY_POINT_LINK_TEXT.THUMBNAIL
                    : LIVESTREAM_ENTRY_POINT_LINK_TEXT.CTA
        });
        sendEvent([{ destination: 'amplitude', eventProps }]);

        if (!isLoggedIn) {
            triggerLogin({ externalReferrer: categoryPath.replace(/^\/+/, '') });
            return;
        }

        router.push(categoryPath);
    };

    return (
        <div className="livestream-page-entry-point" data-testid="livestream-page-entry-point-zone">
            <LiveStreamEntryPoint
                label={label ? { text: label, style: { color: 'var(--livestream-zone-title-color)' } } : undefined}
                backgroundImage={{ src: backgroundImageUrl }}
                preview={{
                    image: { src: previewImageUrl },
                    avatar: avatarUrl ? { src: avatarUrl } : undefined,
                    title: previewTitle
                }}
                details={{
                    promoImage: { src: promoImageUrl }
                }}
                cta={{
                    label: LIVESTREAM_PAGE_ENTRY_POINT_DEFAULTS.CTA_LABEL,
                    onClick: handleCtaClick
                }}
            />
        </div>
    );
};
